import { createContext, useContext, useState } from "react";
import axios from "axios";
import { BASE_URL, HEADERS } from "./constant";
import { useNavigate } from "react-router-dom";

const CommonContext = createContext();

export const CommonContextProvider = ({ children }) => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [isSubscriptionDisabled, setIsSubscriptionDisabled] = useState(false);

  const [user, setUser] = useState(null);
  const [users, setUsers] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [attendanceData, setAttendanceData] = useState({});
  const [ticketStats, setTicketStats] = useState([]);
  const [amcData, setAmcData] = useState({});
  const [customers, setCustomers] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [brandNames, setBrandNames] = useState([]);
  const [toggled, setToggled] = useState(false);
  const [isloading, setLoading] = useState({
    user: false,
    technicians: false,
    tickets: false,
    amc: false,
    customers: false,
  });

  const fetchUser = async () => {
    const userId = localStorage.getItem("userId");

    if (!userId) {
      localStorage.clear();
      navigate("/");
      return;
    }

    setLoading((prev) => ({ ...prev, user: true }));

    try {
      const response = await axios.get(
        `${BASE_URL}/users/tms_users/${userId}`,
        { headers: HEADERS },
      );

      const fetchedUser = response.data;

      if (!fetchedUser.is_active) {
        localStorage.clear();
        navigate("/");
        return;
      }

      setUser(fetchedUser);

      const todaysAttendance = fetchedUser?.today_attendance;

      if (todaysAttendance?.punch_in) {
        const formattedDate = new Date(todaysAttendance.punch_in)
          .toISOString()
          .split("T")[0];

        localStorage.setItem("punch_in", formattedDate);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      localStorage.clear();
      navigate("/");
    } finally {
      setLoading((prev) => ({ ...prev, user: false }));
    }
  };

  const fetchAllusers = async () => {
    setLoading((prev) => ({ ...prev, users: true }));
    try {
      const response = await axios.get(`${BASE_URL}/users/tms_users/`, {
        headers: HEADERS,
      });
      if (response.data?.results) {
        setUsers(response.data.results);
      }
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, users: false }));
    }
  };

  const fetchSubscription = async () => {
    const userId = localStorage.getItem("userId");

    if (!userId) return;

    setSubscriptionLoading(true);

    try {
      const response = await axios.get(
        `${BASE_URL}/users/subscriptions/?admin_user=${userId}`,
        { headers: HEADERS },
      );

      const sub = response?.data?.results?.[0];

      if (!sub) {
        setSubscription(null);
        return;
      }

      setSubscription(sub);

      if (sub.status === "expired") {
        localStorage.setItem("subscription_status", "expired");
        setIsSubscriptionDisabled(true);
        return;
      }

      if (sub.status === "active") {
        localStorage.setItem("subscription_status", "active");
        setIsSubscriptionDisabled(false);
      }

      if (sub.status === "trial") {
        localStorage.setItem("subscription_status", "trial");
        setIsSubscriptionDisabled(false);
      }
    } catch (err) {
      console.error("Subscription fetch failed", err);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const fetchTechnicians = async () => {
    setLoading((prev) => ({ ...prev, technicians: true }));
    try {
      const response = await axios.get(
        `${BASE_URL}/users/tms_users/?role=technician&page_size=all`,
        { headers: HEADERS },
      );
      if (response.data?.results) {
        setTechnicians(response.data.results);
        calculateAttendance(response.data.results);
      }
    } catch (error) {
      console.error("Error fetching technicians:", error);
    } finally {
      setLoading((prev) => ({ ...prev, technicians: false }));
    }
  };

  const fetchStocks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/api/inventory/`, {
        headers: HEADERS,
      });
      setStocks(response.data.results || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    setLoading((prev) => ({ ...prev, customers: true }));
    try {
      const response = await axios.get(
        `${BASE_URL}/users/tms_users/?role=customer&page_size=all`,
        { headers: HEADERS },
      );
      if (response.data?.results) {
        setCustomers(response.data.results);

        const customerBrands = response.data.results.flatMap((customer) =>
          customer.brand_names
            ? customer.brand_names.map((brand) => ({
                value: brand,
                label: brand,
              }))
            : [],
        );
        setBrandNames(customerBrands);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading((prev) => ({ ...prev, customers: false }));
    }
  };

  const fetchTickets = async () => {
    setLoading((prev) => ({ ...prev, tickets: true }));
    try {
      const response = await axios.get(
        `${BASE_URL}/api/tickets/?page_size=all`,
        { headers: HEADERS },
      );
      if (response.data?.results) {
        setTickets(response.data.results);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setLoading((prev) => ({ ...prev, tickets: false }));
    }
  };

  const fetchAmcData = async () => {
    setLoading((prev) => ({ ...prev, amc: true }));
    try {
      const response = await axios.get(`${BASE_URL}/api/amcs/?page_size=all`, {
        headers: HEADERS,
      });
      if (response.data?.results) {
        const amcResults = response.data.results || [];
        const totalCount = amcResults.length;
        const upcomingCount = amcResults.filter(
          (amc) => amc.status === "Upcoming",
        ).length;
        const renewalCount = amcResults.filter(
          (amc) => amc.status === "Renewal",
        ).length;
        const completedCount = amcResults.filter(
          (amc) => amc.status === "Completed",
        ).length;
        const expiredCount = amcResults.filter(
          (amc) => amc.status === "Expired",
        ).length;

        setAmcData({
          totalCount,
          upcomingCount,
          expiredCount,
          renewalCount,
          completedCount,
        });
      }
    } catch (error) {
      console.error("Error fetching AMC details:", error);
    } finally {
      setLoading((prev) => ({ ...prev, amc: false }));
    }
  };

  const calculateAttendance = async () => {
    setLoading(true);
    try {
      const url = `${BASE_URL}/users/tms_users/attendance_counts/`;

      const response = await axios.get(url, {
        headers: HEADERS,
      });
      if (response.data) {
        setAttendanceData(response.data);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/api/tickets/ticket_count/`,
        { headers: HEADERS },
      );
      if (response.data) {
        setTicketStats(response.data);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setLoading((prev) => ({ ...prev, tickets: false }));
    }
  };

  return (
    <CommonContext.Provider
      value={{
        user,
        users,
        technicians,
        tickets,
        stocks,
        attendanceData,
        ticketStats,
        amcData,
        customers,
        brandNames,
        isloading,
        subscription,
        subscriptionLoading,
        isSubscriptionDisabled,
        fetchSubscription,
        fetchUser,
        fetchAllusers,
        fetchTechnicians,
        fetchStocks,
        fetchTicketStatus,
        fetchTickets,
        fetchAmcData,
        fetchCustomers,
        toggled,
        setToggled,
        setBrandNames,
      }}
    >
      {children}
    </CommonContext.Provider>
  );
};

export const useCommonContext = () => useContext(CommonContext);
